import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  EventNote as VisitIcon,
  Healing as SymptomIcon,
  MonitorHeart as VitalsIcon,
  BloodtypeOutlined as GlucoseIcon,
  Science as LabIcon,
  ExpandMore as ExpandMoreIcon,
  FileUpload as UploadIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  AssignmentTurnedIn as DiagnosisIcon,
  CheckCircle as ValidateIcon,
  Cancel as RejectIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import FormField from '../../Common/FormField';
import SelectField from '../../Common/SelectField';
import SymptomCheckboxGroup from '../components/SymptomCheckboxGroup';
import SymptomAjaxMultiSelect from '../components/SymptomAjaxMultiSelect';
import LabReportUpload from '../../LabReportUpload/LabReportUpload';
import { useFormData } from '../../../contexts/FormDataContext';
import { useSession } from '../../../contexts/SessionContext';
import { useAmbientListening } from '../../../contexts/AmbientListeningContext';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import {
  diagnosisAPI,
  type DiagnosisDisorderSearchRow,
  type DiagnosisResolveIcd11Response,
  type DxCandidate,
  type DxFinalSelection,
  type DxRunSummary,
  type SessionTriageAssessment,
  type SessionDiagnosisSelection,
  type TriageLevelDefinition,
} from '../../../services/api';

interface Tab2Props {
  sessionId: string;
}

interface SecondaryDiagnosisEntry {
  slot: string;
  dsmInput: string;
  selectedDisorder: DiagnosisDisorderSearchRow | null;
  selectedPmoId: number | '';
  status: string;
  resolution: DiagnosisResolveIcd11Response | null;
  loading: boolean;
  message?: string | null;
}

const TRIAGE_LEVEL_ORDER: Array<'1' | '2' | '3' | '4_5'> = ['1', '2', '3', '4_5'];
const TRIAGE_LEVELS_FALLBACK: TriageLevelDefinition[] = [
  {
    level: '1',
    label: 'Level 1 (Highest Urgency)',
    clinical_presentation: 'Immediate life/organ threat, severe agitation or instability',
    ed_disposition: 'Immediate ED intervention and continuous monitoring',
  },
  {
    level: '2',
    label: 'Level 2 (High Risk)',
    clinical_presentation: 'High-risk condition requiring rapid assessment',
    ed_disposition: 'Urgent ED evaluation and close observation',
  },
  {
    level: '3',
    label: 'Level 3 (Urgent)',
    clinical_presentation: 'Stable but requires timely diagnostic/therapeutic resources',
    ed_disposition: 'ED treatment with prioritized but non-immediate workflow',
  },
  {
    level: '4_5',
    label: 'Level 4/5 (Lower Urgency)',
    clinical_presentation: 'Low acuity presentation with minimal immediate risk',
    ed_disposition: 'Routine ED pathway or outpatient-safe plan per clinician judgement',
  },
];

// Tab name must match backend TAB_NAME_MAPPING["tab2_consultation"]
const TAB_NAME = 'Consultation';

/* Design: section and form field border #8DBAEA; headers #012676 */
const SECTION_BORDER = '#8DBAEA';
const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: SECTION_BORDER, borderWidth: 1 },
    '&:hover fieldset': { borderColor: '#7aa8e0' },
    '&.Mui-focused fieldset': { borderColor: '#012676', borderWidth: 1 },
  },
};
const SECTION_HEADER_COLOR = '#012676';

/** Temporary: set `true` when the AJAX symptoms trial is ready to show again. */
const SHOW_SYMPTOM_AJAX_TRIAL = false;

/** Slightly above backend `DIAGNOSIS_RECOMPUTE_DEBOUNCE_MS` (default 4s) so polls see completed runs. */
const DX_RUNTIME_POLL_MS = 6000;
/** Min interval between automatic LLM triage suggests while mic is active (reduces duplicate LLM cost). */
const TRIAGE_LIVE_THROTTLE_MS = 12000;

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

// export default function Tab2_Consultation({ sessionId }: Tab2Props) {

export default function Tab2_Consultation({ sessionId }: Tab2Props) {
  const [labUploadOpen, setLabUploadOpen] = useState(false);
  const [dsmInput, setDsmInput] = useState('');
  const [dsmOptions, setDsmOptions] = useState<DiagnosisDisorderSearchRow[]>([]);
  const [dsmLoading, setDsmLoading] = useState(false);
  const [selectedDisorder, setSelectedDisorder] = useState<DiagnosisDisorderSearchRow | null>(null);
  const [modifierOptions, setModifierOptions] = useState<Array<{ pmo_id: number; label: string }>>([]);
  const [selectedPmoId, setSelectedPmoId] = useState<number | ''>('');
  const [resolution, setResolution] = useState<DiagnosisResolveIcd11Response | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState<string | null>(null);
  const [diagnosisActionMsg, setDiagnosisActionMsg] = useState<string | null>(null);
  const [secondaryDiagnoses, setSecondaryDiagnoses] = useState<SecondaryDiagnosisEntry[]>([]);
  const [dxRun, setDxRun] = useState<DxRunSummary | null>(null);
  const [dxCandidates, setDxCandidates] = useState<DxCandidate[]>([]);
  const [dxFinalSelections, setDxFinalSelections] = useState<DxFinalSelection[]>([]);
  const [dxLoading, setDxLoading] = useState(false);
  const [secondarySearchLoadingSlot, setSecondarySearchLoadingSlot] = useState<string | null>(null);
  const [savedSelections, setSavedSelections] = useState<SessionDiagnosisSelection[]>([]);
  const [triageLevels, setTriageLevels] = useState<TriageLevelDefinition[]>([]);
  const [triageAssessment, setTriageAssessment] = useState<SessionTriageAssessment | null>(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [triageSaving, setTriageSaving] = useState(false);

  const { getFieldValue, updateField } = useFormData();
  const { isEditingHistoricalForms } = useSession();
  const { state: ambientListeningState } = useAmbientListening();
  const { canAccess } = useFeatureAccess();
  const canViewDiagnosisSection = canAccess('patient_visit.diagnosis_section');
  const prevAmbientListeningRef = useRef(ambientListeningState);
  const triageAssessmentRef = useRef<SessionTriageAssessment | null>(null);
  const lastLiveTriageSuggestAtRef = useRef(0);

  useEffect(() => {
    triageAssessmentRef.current = triageAssessment;
  }, [triageAssessment]);

  const consultArrival = getFieldValue(TAB_NAME, 'arrival_time', '');
  const consultVisitDate = getFieldValue(TAB_NAME, 'date_of_visit', '');

  // Patient Visit "Visit Date": set today only for new/live visits when empty.
  useEffect(() => {
    if (isEditingHistoricalForms) return;
    if (consultVisitDate != null && String(consultVisitDate).trim() !== '') return;
    updateField(TAB_NAME, 'date_of_visit', todayISO());
  }, [consultVisitDate, isEditingHistoricalForms, updateField]);

  // Patient Visit "Arrival Time": current time only when empty.
  useEffect(() => {
    if (isEditingHistoricalForms) return;
    if (consultArrival != null && String(consultArrival).trim() !== '') return;

    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    updateField(TAB_NAME, 'arrival_time', `${hh}:${mm}`);
  }, [consultArrival, isEditingHistoricalForms, updateField]);

  const diagnosisStatus = getFieldValue(TAB_NAME, 'primary_diagnosis_status', '');

  const selectedModifierLabel = useMemo(
    () => modifierOptions.find((m) => m.pmo_id === selectedPmoId)?.label ?? '',
    [modifierOptions, selectedPmoId],
  );
  const displayTriageLevels = useMemo(() => {
    if (triageLevels.length > 0) return triageLevels;
    return TRIAGE_LEVELS_FALLBACK;
  }, [triageLevels]);

  const fetchDsmOptions = useCallback(async (q: string) => {
    const query = q.trim();
    if (query.length < 2) {
      setDsmOptions([]);
      return;
    }
    setDsmLoading(true);
    try {
      const { data } = await diagnosisAPI.searchDsm(query, 30);
      setDsmOptions(data || []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'DSM search failed';
      setDiagnosisError(String(msg));
    } finally {
      setDsmLoading(false);
    }
  }, []);

  const resolveMapping = useCallback(
    async (disorderId: number, pmoId?: number | null, fallbackCode?: string | null) => {
    setResolveLoading(true);
    setDiagnosisError(null);
    try {
      const { data } = await diagnosisAPI.resolveIcd11(disorderId, pmoId ?? null);
      setResolution(data);
      updateField(TAB_NAME, 'primary_diagnosis_code', data.primary_code || fallbackCode || '');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Unable to resolve ICD-11 mapping';
      setDiagnosisError(String(msg));
      setResolution(null);
    } finally {
      setResolveLoading(false);
    }
    },
    [updateField],
  );

  useEffect(() => {
    const q = dsmInput.trim();
    if (q.length < 2) {
      setDsmOptions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void fetchDsmOptions(q);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dsmInput, fetchDsmOptions]);

  useEffect(() => {
    let cancelled = false;
    diagnosisAPI
      .listSelections(sessionId)
      .then(async ({ data }) => {
        if (cancelled) return;
        setSavedSelections(data || []);
        const primary = data.find((s) => s.slot === 'primary');
        if (primary?.dsmv_disorder_id) {
          const disorder = await diagnosisAPI.getDisorder(primary.dsmv_disorder_id);
          if (cancelled) return;
          const picked: DiagnosisDisorderSearchRow = {
            disorder_id: primary.dsmv_disorder_id,
            disorder_name: disorder.data.disorder_name ?? undefined,
            icd_10: disorder.data.icd_10 ?? undefined,
            icd_11: disorder.data.icd_11 ?? undefined,
            category_id: disorder.data.category_id ?? undefined,
            category_name: disorder.data.category_name ?? undefined,
          };
          setSelectedDisorder(picked);
          if (picked.disorder_name) setDsmInput(picked.disorder_name);
          const mods = (disorder.data.modifiers || []).map((m) => ({
            pmo_id: m.pmo_id,
            label: [m.property_key, m.property_value].filter(Boolean).join(' - ') || `pmo ${m.pmo_id}`,
          }));
          setModifierOptions(mods);
          const pmo = primary.pmo_id ?? null;
          setSelectedPmoId(pmo ?? '');
          if (primary.diagnosis_status) {
            updateField(TAB_NAME, 'primary_diagnosis_status', primary.diagnosis_status);
          }
          await resolveMapping(primary.dsmv_disorder_id, pmo, primary.icd11_code || picked.icd_11 || picked.icd_10 || '');
          if (primary.icd11_code) {
            updateField(TAB_NAME, 'primary_diagnosis_code', primary.icd11_code);
          } else if (picked.icd_11 || picked.icd_10) {
            updateField(TAB_NAME, 'primary_diagnosis_code', picked.icd_11 || picked.icd_10 || '');
          }
        }

        const secondaries = data
          .filter((s) => s.slot.startsWith('secondary_'))
          .sort((a, b) => a.slot.localeCompare(b.slot));
        const mapped: SecondaryDiagnosisEntry[] = [];
        for (const s of secondaries) {
          if (!s.dsmv_disorder_id) continue;
          const d = await diagnosisAPI.getDisorder(s.dsmv_disorder_id);
          const selected: DiagnosisDisorderSearchRow = {
            disorder_id: s.dsmv_disorder_id,
            disorder_name: d.data.disorder_name ?? undefined,
            icd_10: d.data.icd_10 ?? undefined,
            icd_11: d.data.icd_11 ?? undefined,
            category_id: d.data.category_id ?? undefined,
            category_name: d.data.category_name ?? undefined,
          };
          mapped.push({
            slot: s.slot,
            dsmInput: selected.disorder_name || '',
            selectedDisorder: selected,
            selectedPmoId: s.pmo_id ?? '',
            status: s.diagnosis_status || 'Provisional',
            resolution: s.icd11_code
              ? {
                  primary_code: s.icd11_code,
                  primary_source: 'saved_selection',
                  icd10_code: s.icd10_code || undefined,
                  disorder_name: selected.disorder_name,
                  category_name: selected.category_name,
                  alternates: [],
                }
              : null,
            loading: false,
            message: null,
          });
        }
        if (!cancelled) setSecondaryDiagnoses(mapped);
      })
      .catch(() => {
        /* no prior diagnosis selection */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, resolveMapping, updateField]);

  const refreshSavedSelections = useCallback(async () => {
    try {
      const { data } = await diagnosisAPI.listSelections(sessionId);
      setSavedSelections(data || []);
    } catch {
      setSavedSelections([]);
    }
  }, [sessionId]);

  const loadDisorder = async (row: DiagnosisDisorderSearchRow) => {
    setDiagnosisActionMsg(null);
    setSelectedDisorder(row);
    updateField(TAB_NAME, 'primary_diagnosis', row.disorder_name || '');
    updateField(TAB_NAME, 'dsm_disorder_id', String(row.disorder_id));
    updateField(TAB_NAME, 'primary_diagnosis_code', row.icd_11 || row.icd_10 || '');
    setSelectedPmoId('');
    try {
      const { data } = await diagnosisAPI.getDisorder(row.disorder_id);
      const mods = (data.modifiers || []).map((m) => ({
        pmo_id: m.pmo_id,
        label: [m.property_key, m.property_value].filter(Boolean).join(' - ') || `pmo ${m.pmo_id}`,
      }));
      setModifierOptions(mods);
    } catch {
      setModifierOptions([]);
    }
    await resolveMapping(row.disorder_id, null, row.icd_11 || row.icd_10 || '');
  };

  const saveDiagnosisSelection = async (accepted: boolean) => {
    if (!selectedDisorder) {
      setDiagnosisActionMsg('Select primary diagnosis first.');
      return;
    }
    try {
      const clinicalNarrative = getFieldValue(TAB_NAME, 'clinical_notes', '') || null;
      const summaryNarrative = getFieldValue(TAB_NAME, 'preconsultation_summary_notes', '') || null;
      const resolvedNotes = summaryNarrative || clinicalNarrative;
      const { data } = await diagnosisAPI.putSelection(sessionId, {
        slot: 'primary',
        dsmv_disorder_id: selectedDisorder.disorder_id,
        pmo_id: selectedPmoId === '' ? null : selectedPmoId,
        icd11_code: resolution?.primary_code ?? null,
        icd10_code: resolution?.icd10_code ?? null,
        diagnosis_status: diagnosisStatus || null,
        ai_suggestion_accepted: accepted,
        assessment_text: clinicalNarrative,
        notes: resolvedNotes,
        finalize: true,
      });
      if (data.diagnosis_status) {
        updateField(TAB_NAME, 'primary_diagnosis_status', data.diagnosis_status);
      }
      if (data.icd11_code || data.icd10_code) {
        updateField(TAB_NAME, 'primary_diagnosis_code', data.icd11_code || data.icd10_code || '');
      }
      await refreshClinicalDxUi();
      setDiagnosisActionMsg(accepted ? 'Diagnosis validated and saved.' : 'Suggestion rejected and saved.');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Could not save diagnosis';
      setDiagnosisActionMsg(String(msg));
    }
  };

  const addSecondaryDiagnosis = () => {
    const nextIndex = secondaryDiagnoses.length + 1;
    setSecondaryDiagnoses((prev) => [
      ...prev,
      {
        slot: `secondary_${nextIndex}`,
        dsmInput: '',
        selectedDisorder: null,
        selectedPmoId: '',
        status: 'Provisional',
        resolution: null,
        loading: false,
        message: null,
      },
    ]);
  };

  const updateSecondary = (slot: string, patch: Partial<SecondaryDiagnosisEntry>) => {
    setSecondaryDiagnoses((prev) => prev.map((s) => (s.slot === slot ? { ...s, ...patch } : s)));
  };

  const resolveSecondary = async (slot: string, disorderId: number, pmoId: number | null) => {
    updateSecondary(slot, { loading: true, message: null });
    try {
      const { data } = await diagnosisAPI.resolveIcd11(disorderId, pmoId);
      updateSecondary(slot, { resolution: data, loading: false });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Unable to resolve ICD-11';
      updateSecondary(slot, { loading: false, message: String(msg) });
    }
  };

  const saveSecondary = async (entry: SecondaryDiagnosisEntry) => {
    if (!entry.selectedDisorder) {
      updateSecondary(entry.slot, { message: 'Select diagnosis first.' });
      return;
    }
    try {
      const clinicalNarrative = getFieldValue(TAB_NAME, 'clinical_notes', '') || null;
      const summaryNarrative = getFieldValue(TAB_NAME, 'preconsultation_summary_notes', '') || null;
      const secondaryNotes = summaryNarrative || clinicalNarrative || `Saved from ${entry.slot}`;
      await diagnosisAPI.putSelection(sessionId, {
        slot: entry.slot,
        dsmv_disorder_id: entry.selectedDisorder.disorder_id,
        pmo_id: entry.selectedPmoId === '' ? null : entry.selectedPmoId,
        icd11_code: entry.resolution?.primary_code ?? null,
        icd10_code: entry.resolution?.icd10_code ?? null,
        diagnosis_status: entry.status,
        ai_suggestion_accepted: true,
        assessment_text: clinicalNarrative,
        notes: secondaryNotes,
        finalize: true,
      });
      await refreshClinicalDxUi();
      updateSecondary(entry.slot, { message: 'Saved.' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save secondary diagnosis';
      updateSecondary(entry.slot, { message: String(msg) });
    }
  };

  const removeSecondary = async (slot: string) => {
    try {
      await diagnosisAPI.deleteSelection(sessionId, slot);
    } catch {
      /* ignore */
    }
    await refreshClinicalDxUi();
    setSecondaryDiagnoses((prev) => prev.filter((s) => s.slot !== slot));
  };

  const redoSelectionDecision = async (selection: SessionDiagnosisSelection, accepted: boolean) => {
    if (!selection.dsmv_disorder_id) return;
    try {
      await diagnosisAPI.putSelection(sessionId, {
        slot: selection.slot,
        dsmv_disorder_id: selection.dsmv_disorder_id,
        pmo_id: selection.pmo_id ?? null,
        icd11_code: selection.icd11_code ?? null,
        icd10_code: selection.icd10_code ?? null,
        diagnosis_status: selection.diagnosis_status ?? null,
        ai_suggestion_accepted: accepted,
        assessment_text: selection.assessment_text ?? null,
        notes: selection.notes ?? selection.assessment_text ?? null,
        finalize: true,
      });
      await refreshClinicalDxUi();
      setDiagnosisActionMsg(
        accepted
          ? `Selection ${selection.slot} marked as validated.`
          : `Selection ${selection.slot} marked as rejected.`,
      );
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to update clinician selection';
      setDiagnosisActionMsg(String(msg));
    }
  };

  const loadDxRuntimeData = useCallback(async () => {
    const [runRes, candRes, finalRes] = await Promise.allSettled([
      diagnosisAPI.getLatestRun(sessionId),
      diagnosisAPI.listCandidates(sessionId),
      diagnosisAPI.listFinalSelections(sessionId),
    ]);
    if (runRes.status === 'fulfilled') setDxRun(runRes.value.data);
    if (candRes.status === 'fulfilled') setDxCandidates(candRes.value.data || []);
    if (finalRes.status === 'fulfilled') setDxFinalSelections(finalRes.value.data || []);
  }, [sessionId]);

  const refreshClinicalDxUi = useCallback(async () => {
    await refreshSavedSelections();
    await loadDxRuntimeData();
  }, [refreshSavedSelections, loadDxRuntimeData]);

  const clearPrimaryDiagnosis = useCallback(async () => {
    setDiagnosisActionMsg(null);
    setSelectedDisorder(null);
    setDsmInput('');
    setModifierOptions([]);
    setResolution(null);
    setSelectedPmoId('');
    updateField(TAB_NAME, 'primary_diagnosis', '');
    updateField(TAB_NAME, 'primary_diagnosis_code', '');
    updateField(TAB_NAME, 'dsm_disorder_id', '');
    updateField(TAB_NAME, 'primary_diagnosis_modifier', '');
    try {
      await diagnosisAPI.deleteSelection(sessionId, 'primary');
    } catch {
      /* ignore */
    }
    try {
      await diagnosisAPI.deleteFinalSelection(sessionId, 'primary');
    } catch {
      /* ignore */
    }
    await refreshClinicalDxUi();
  }, [sessionId, updateField, refreshClinicalDxUi]);

  const loadTriageData = useCallback(async () => {
    const [levelsRes, triageRes] = await Promise.allSettled([
      diagnosisAPI.listTriageLevels(),
      diagnosisAPI.getTriage(sessionId),
    ]);
    if (levelsRes.status === 'fulfilled') {
      const sorted = (levelsRes.value.data || []).slice().sort((a, b) => {
        return TRIAGE_LEVEL_ORDER.indexOf(a.level) - TRIAGE_LEVEL_ORDER.indexOf(b.level);
      });
      setTriageLevels(sorted);
    }
    if (triageRes.status === 'fulfilled') {
      setTriageAssessment(triageRes.value.data || null);
    }
  }, [sessionId]);

  /**
   * LLM triage suggest. While mic is live: throttled unless force.
   * On mic stop we pass force=true once (reconciler-style). No further triage runs until mic is live again or user clicks Auto-Classify.
   */
  const suggestTriageAuto = useCallback(
    async (opts?: { silent?: boolean; force?: boolean }) => {
      if (triageAssessmentRef.current?.overridden_by_clinician) return;
      const silent = opts?.silent ?? false;
      const force = opts?.force ?? false;
      const now = Date.now();
      if (!force && now - lastLiveTriageSuggestAtRef.current < TRIAGE_LIVE_THROTTLE_MS) return;
      lastLiveTriageSuggestAtRef.current = now;
      if (!silent) setTriageLoading(true);
      try {
        const { data } = await diagnosisAPI.suggestTriage(sessionId);
        setTriageAssessment(data);
        if (!silent) setDiagnosisActionMsg(`Triage level suggested: ${data.esi_level}`);
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Unable to suggest triage level';
        if (!silent) setDiagnosisActionMsg(String(msg));
      } finally {
        if (!silent) setTriageLoading(false);
      }
    },
    [sessionId],
  );

  const suggestTriageLevel = useCallback(async () => {
    await suggestTriageAuto({ silent: false, force: true });
  }, [suggestTriageAuto]);

  const refreshDxAndTriageReadOnly = useCallback(async () => {
    await loadDxRuntimeData();
    await loadTriageData();
  }, [loadDxRuntimeData, loadTriageData]);

  const saveTriageLevel = useCallback(
    async (level: '1' | '2' | '3' | '4_5') => {
      setTriageSaving(true);
      try {
        const { data } = await diagnosisAPI.putTriage(sessionId, {
          esi_level: level,
          confidence: triageAssessment?.confidence ?? null,
          risk_score: triageAssessment?.risk_score ?? null,
          trigger_flags_json: triageAssessment?.trigger_flags_json ?? null,
          structured_features_json: triageAssessment?.structured_features_json ?? null,
          rationale: triageAssessment?.rationale ?? null,
          recommended_action: triageAssessment?.recommended_action ?? null,
          source: 'manual',
          override_reason: 'Clinician selected triage level from diagnosis section',
        });
        setTriageAssessment(data);
      } finally {
        setTriageSaving(false);
      }
    },
    [sessionId, triageAssessment],
  );

  useEffect(() => {
    void refreshDxAndTriageReadOnly();
  }, [refreshDxAndTriageReadOnly]);

  useEffect(() => {
    if (isEditingHistoricalForms) return;

    const prev = prevAmbientListeningRef.current;
    prevAmbientListeningRef.current = ambientListeningState;

    const pollLive = async () => {
      await loadDxRuntimeData();
      await suggestTriageAuto({ silent: true, force: false });
    };

    if (ambientListeningState === 'listening' || ambientListeningState === 'processing') {
      void pollLive();
      const id = window.setInterval(() => void pollLive(), DX_RUNTIME_POLL_MS);
      return () => window.clearInterval(id);
    }

    if ((prev === 'listening' || prev === 'processing') && ambientListeningState === 'idle') {
      void loadDxRuntimeData();
      void suggestTriageAuto({ silent: true, force: true });
      const t1 = window.setTimeout(() => void loadDxRuntimeData(), 1500);
      const t2 = window.setTimeout(() => void loadDxRuntimeData(), 5000);
      const t3 = window.setTimeout(() => void loadDxRuntimeData(), 12000);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
        window.clearTimeout(t3);
      };
    }

    return undefined;
  }, [ambientListeningState, isEditingHistoricalForms, loadDxRuntimeData, suggestTriageAuto]);

  const triggerDiagnosisRun = async () => {
    setDxLoading(true);
    setDiagnosisActionMsg(null);
    try {
      const { data } = await diagnosisAPI.triggerRun(sessionId, 'manual_ui');
      if (data.triggered) {
        setDiagnosisActionMsg('AI diagnosis recomputed.');
      } else if (data.reason === 'debounced') {
        setDiagnosisActionMsg('AI recompute skipped (debounced).');
      } else if (data.reason === 'engine_disabled') {
        setDiagnosisActionMsg('Diagnosis engine is disabled.');
      } else {
        setDiagnosisActionMsg(`AI recompute skipped: ${data.reason || 'unknown reason'}`);
      }
      await refreshDxAndTriageReadOnly();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Unable to trigger diagnosis run';
      setDiagnosisActionMsg(String(msg));
    } finally {
      setDxLoading(false);
    }
  };

  const computeNextAiSecondarySlot = (): string => {
    const nums = dxFinalSelections
      .map((s) => /^ai_secondary_(\d+)$/.exec(s.slot))
      .filter((m): m is RegExpExecArray => Boolean(m))
      .map((m) => Number(m[1]));
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return `ai_secondary_${next}`;
  };

  const applyCandidateAsFinal = async (candidate: DxCandidate, mode: 'primary' | 'secondary' | 'reject') => {
    try {
      const mappedPrimary = candidate.maps?.[0]?.mapped_condition_id ?? null;
      const slot =
        mode === 'primary'
          ? 'primary'
          : mode === 'secondary'
            ? computeNextAiSecondarySlot()
            : `rej_${candidate.condition_id}`;
      await diagnosisAPI.putFinalSelection(sessionId, {
        slot,
        selected_condition_id: candidate.condition_id,
        selected_icd11_condition_id: mappedPrimary,
        status: mode === 'reject' ? 'rejected' : 'provisional',
        accepted_ai: mode !== 'reject',
        notes:
          mode === 'primary'
            ? 'Accepted as primary from AI candidate list (awaiting clinician confirmation)'
            : mode === 'secondary'
              ? 'Accepted as secondary from AI candidate list (awaiting clinician confirmation)'
              : 'Rejected AI suggestion',
      });
      setDiagnosisActionMsg(
        mode === 'reject' ? 'Candidate marked as rejected.' : 'Saved to clinician diagnosis list.',
      );
      await refreshClinicalDxUi();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to update final selection';
      setDiagnosisActionMsg(String(msg));
    }
  };

  const redoDxFinalSelection = async (row: DxFinalSelection, accepted: boolean) => {
    if (row.selected_condition_id == null) return;
    try {
      await diagnosisAPI.putFinalSelection(sessionId, {
        slot: row.slot,
        selected_condition_id: row.selected_condition_id,
        selected_icd11_condition_id: row.selected_icd11_condition_id ?? undefined,
        status: accepted ? 'confirmed' : 'rejected',
        accepted_ai: accepted,
        notes: row.notes ?? undefined,
      });
      await refreshClinicalDxUi();
      setDiagnosisActionMsg(
        accepted ? `AI slot ${row.slot} marked confirmed.` : `AI slot ${row.slot} marked rejected.`,
      );
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to update AI final selection';
      setDiagnosisActionMsg(String(msg));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 8 }}>
      {/* 1. Visit Information */}
      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${SECTION_BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <VisitIcon sx={{ fontSize: 20, mr: 1, color: SECTION_HEADER_COLOR }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: SECTION_HEADER_COLOR }}>
            Visit Information
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {/* Date of Visit */}
          {/* Date of Visit */}
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="date_of_visit"
              label="Visit Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="arrival_time"
              label="Arrival Time"
              type="time"
              required
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} />
            {/* OLD: Free-text FormField
            <FormField
              tabName={TAB_NAME}
              fieldName="reason_for_visit"
              label="Reason for Visit"
              placeholder="Routine Visit / Quarterly Visit / Annual Visit / Collecting insulin / Other"
              size="small"
              fullWidth
            /> */}
            {/* OLD: Free-text FormField
            <FormField
              tabName={TAB_NAME}
              fieldName="reason_for_visit"
              label="Reason for Visit"
              placeholder="Routine Visit / Quarterly Visit / Annual Visit / Collecting insulin / Other"
              size="small"
              fullWidth
            /> */}          
          {/* Reason for Visit – dropdown only (free-text reason removed) */}
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              tabName={TAB_NAME}
              fieldName="reason_for_visit"
              label="Reason for Visit"
              options={[
                { value: 'Routine Visit', label: 'Routine Visit' },
                { value: 'Quarterly Visit', label: 'Quarterly Visit' },
                { value: 'Annual Visit', label: 'Annual Visit' },
                { value: 'Collecting insulin', label: 'Collecting insulin' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="reason_other"
              label="Reason (if Other)"
              placeholder="Specify other reason"
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 2. Patient Vitals (includes Pre-consultation Summary & Notes) */}
      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${SECTION_BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <VitalsIcon sx={{ fontSize: 20, mr: 1, color: SECTION_HEADER_COLOR }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: SECTION_HEADER_COLOR }}>
            Patient Vitals
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="weight_kg" label="Weight (Kg)" placeholder="Enter weight in kg" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="height_cm" label="Height (cm)" placeholder="Enter height in cm" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="bmi_z_score" label="BMI Z Score" placeholder="Enter BMI Z Score" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="bmi" label="BMI (Kg/m²)" placeholder="Auto-calculated" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="blood_pressure_systolic" label="BP Systolic (mmHg)" placeholder="Enter systolic BP" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <FormField tabName={TAB_NAME} fieldName="blood_pressure_diastolic" label="BP Diastolic (mmHg)" placeholder="Enter diastolic BP" type="number" size="small" fullWidth sx={formFieldSx} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: SECTION_HEADER_COLOR, mb: 0.5, display: 'block' }}>
            Pre-consultation Summary & Notes
          </Typography>
          <FormField
            tabName={TAB_NAME}
            fieldName="preconsultation_summary_notes"
            label=""
            placeholder="Enter pre-consultation notes and observations"
            multiline
            rows={4}
            size="small"
            fullWidth
            sx={formFieldSx}
          />
        </Box>
      </Paper>

      {/* 3. Clinical Analysis: header, sub-header (Current Symptoms), then options */}
      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${SECTION_BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <SymptomIcon sx={{ fontSize: 20, mr: 1, color: SECTION_HEADER_COLOR }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: SECTION_HEADER_COLOR }}>
            Clinical Analysis
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <ScheduleIcon sx={{ fontSize: 18, mr: 1, color: SECTION_HEADER_COLOR }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: SECTION_HEADER_COLOR }}>
            {/* Current Symptoms (Select all that apply) */}
            Current Symptoms
          </Typography>
        </Box>
        {/* <SymptomCheckboxGroup tabName={TAB_NAME} />
        {SHOW_SYMPTOM_AJAX_TRIAL && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: SECTION_HEADER_COLOR, mb: 0.5, display: 'block' }}>
              Symptoms (New AJAX Multi-Select Trial)
            </Typography> */}
            <SymptomAjaxMultiSelect tabName={TAB_NAME} />
          {/* </>
        )} */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: SECTION_HEADER_COLOR, mb: 0.5, display: 'block' }}>
          Other Symptoms
        </Typography>
        <FormField
          tabName={TAB_NAME}
          fieldName="other_symptoms"
          label=""
          placeholder="Describe any other symptoms not listed above"
          multiline
          rows={2}
          size="small"
          fullWidth
          sx={formFieldSx}
        />
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: SECTION_HEADER_COLOR, mb: 0.5, display: 'block' }}>
          Clinical Notes
        </Typography>
        <FormField
          tabName={TAB_NAME}
          fieldName="clinical_notes"
          label=""
          placeholder="Clinical notes and assessment narrative"
          multiline
          rows={3}
          size="small"
          fullWidth
          sx={formFieldSx}
        />
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: SECTION_HEADER_COLOR, mb: 1, display: 'block' }}>
          Blood Glucose Readings
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <FormField
              tabName={TAB_NAME}
              fieldName="max_fasting_blood_glucose"
              label="Maximum Value of Fasting Blood Glucose" //.. maximum value of testing blood glucose
              placeholder="Enter value"
              type="number"
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              tabName={TAB_NAME}
              fieldName="max_post_prandial_blood_glucose"
              label="Maximum Value of Post Prandial Blood Glucose"
              placeholder="Enter value"
              type="number"
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 4. Lab Values */}
      <Accordion
        defaultExpanded
        elevation={0}
        sx={{
          mb: 2,
          border: `1px solid ${SECTION_BORDER}`,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { mb: 2 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '44px',
            '&.Mui-expanded': { minHeight: '44px' },
            bgcolor: 'grey.50',
          }}
        >
          {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LabIcon sx={{ fontSize: 20, mr: 1, color: 'secondary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Lab Report Values
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              (Auto-populated from uploaded reports)
            </Typography>
          </Box> */}

          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <LabIcon sx={{ fontSize: 20, mr: 1, color: '#0D47A1' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Lab Report Values
            </Typography>
            {/* Spacer pushes icon to the right */}
            <Box sx={{ flex: 1 }} />
            
            <Tooltip title="Upload lab report to auto-populate values">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent accordion toggle
                  setLabUploadOpen(true);
                }}
                sx={{
                  ml: 1,
                  // color: 'secondary.main',
                  color: '#FFFFFF',
                  // bgcolor: 'secondary.light',
                  bgcolor: '#0D47A1', 
                  width: 28,
                  height: 28,
                  '&:hover': { bgcolor: '#1565C0', color: 'white' },
                }}
              >
                <UploadIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {/* <Box sx={{ flex: 1 }} /> */}
          </Box>

        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5, pb: 8 , maxHeight: 'none'}}>
          {/* HbA1c and eAG */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
            Diabetes Markers
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormField tabName={TAB_NAME} fieldName="hba1c_percent" label="HbA1C (%)" placeholder="Enter HbA1C percentage" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField tabName={TAB_NAME} fieldName="estimated_avg_blood_glucose" label="Estimated Average Blood Glucose (eAG)" placeholder="Enter eAG value" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SelectField tabName={TAB_NAME} fieldName="cbc_status" label="Complete Blood Count" options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* CBC Values */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
            Complete Blood Count (CBC)
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={3}>
              <FormField tabName={TAB_NAME} fieldName="haemoglobin_g_l" label="Hemoglobin (g/L)" placeholder="e.g., 140" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <FormField tabName={TAB_NAME} fieldName="rbc_10_12_l" label="RBC (x10^12/L)" placeholder="e.g., 4.5" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <FormField tabName={TAB_NAME} fieldName="wbc_10_9_l" label="WBC (x10^9/L)" placeholder="e.g., 7.5" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <FormField tabName={TAB_NAME} fieldName="platelets_10_9_l" label="Platelets (x10^9/L)" placeholder="e.g., 250" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Differential Count */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
            Differential Count
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4} md={2.4}>
              <FormField tabName={TAB_NAME} fieldName="neutrophils" label="Neutrophils (%)" placeholder="e.g., 60" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <FormField tabName={TAB_NAME} fieldName="lymphocytes" label="Lymphocytes (%)" placeholder="e.g., 30" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <FormField tabName={TAB_NAME} fieldName="monocytes" label="Monocytes (%)" placeholder="e.g., 5" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <FormField tabName={TAB_NAME} fieldName="eosinophils" label="Eosinophils (%)" placeholder="e.g., 3" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <FormField tabName={TAB_NAME} fieldName="basophils" label="Basophils (%)" placeholder="e.g., 1" type="number" size="small" fullWidth sx={formFieldSx} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
            Radiological findings
          </Typography>
          <FormField
            tabName={TAB_NAME}
            fieldName="radiological_findings"
            label=""
            placeholder="Imaging findings (X-ray, USG, CT, MRI) from report or consultation"
            multiline
            rows={3}
            size="small"
            fullWidth
            sx={formFieldSx}
          />
        </AccordionDetails>
      </Accordion>
      {canViewDiagnosisSection && (
      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${SECTION_BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <DiagnosisIcon sx={{ fontSize: 20, mr: 1, color: SECTION_HEADER_COLOR }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: SECTION_HEADER_COLOR }}>
            Diagnosis
          </Typography>
        </Box>

        {diagnosisError && (
          <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setDiagnosisError(null)}>
            {diagnosisError}
          </Alert>
        )}
        {diagnosisActionMsg && (
          <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setDiagnosisActionMsg(null)}>
            {diagnosisActionMsg}
          </Alert>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 0.75, alignItems: { sm: 'flex-start' } }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 0 }}
                options={dsmOptions}
                loading={dsmLoading}
                value={selectedDisorder}
                disableClearable={false}
                isOptionEqualToValue={(a, b) => a.disorder_id === b.disorder_id}
                getOptionLabel={(o) =>
                  o.disorder_name
                    ? `${o.disorder_name} (${o.icd_11 || o.icd_10 || 'code n/a'})`
                    : `#${o.disorder_id}`
                }
                onChange={(_, value) => {
                  if (!value) {
                    void clearPrimaryDiagnosis();
                    return;
                  }
                  void loadDisorder(value);
                }}
                inputValue={dsmInput}
                onInputChange={(_, value, reason) => {
                  setDsmInput(value);
                  if (reason === 'clear') void clearPrimaryDiagnosis();
                }}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary diagnosis"
                    size="small"
                    placeholder="Search DSM diagnosis"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dsmLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={formFieldSx}
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => void clearPrimaryDiagnosis()}
                disabled={!selectedDisorder}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: { xs: 0, sm: 0.5 } }}
              >
                Remove selection
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <SelectField
              tabName={TAB_NAME}
              fieldName="primary_diagnosis_status"
              label="Primary diagnosis Status"
              options={[
                { value: 'Provisional', label: 'Provisional' },
                { value: 'Confirmed', label: 'Confirmed' },
                { value: 'Differential', label: 'Differential' },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormField
              tabName={TAB_NAME}
              fieldName="primary_diagnosis_code"
              label="Primary diagnosis CODE"
              placeholder="ICD-11 code"
              size="small"
              fullWidth
              sx={formFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl size="small" fullWidth disabled={triageSaving} sx={formFieldSx}>
              <InputLabel id="psych-ed-triage-level-label" shrink>
                Psych ED Triage Level
              </InputLabel>
              <Select
                labelId="psych-ed-triage-level-label"
                value={triageAssessment?.esi_level ?? ''}
                label="Psych ED Triage Level"
                displayEmpty
                notched
                renderValue={(value) => {
                  const current = String(value || '');
                  if (!current) return 'Not classified - use Auto-Classify or select level';
                  return displayTriageLevels.find((l) => l.level === current)?.label || current;
                }}
                onChange={(e) => {
                  const value = e.target.value as '' | '1' | '2' | '3' | '4_5';
                  if (!value) return;
                  void saveTriageLevel(value);
                }}
              >
                <MenuItem value="">
                  <em>Not classified - use Auto-Classify or select level</em>
                </MenuItem>
                {displayTriageLevels.map((l) => (
                  <MenuItem key={l.level} value={l.level}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, px: 1.75 }}>
                {triageAssessment
                  ? `Source: ${triageAssessment.source} • Score: ${triageAssessment.risk_score ?? 'n/a'} • Confidence: ${
                      triageAssessment.confidence != null ? Number(triageAssessment.confidence).toFixed(2) : 'n/a'
                    }`
                  : 'Not classified - click Auto-Classify Level or pick a level manually.'}
              </Typography>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void suggestTriageLevel()}
                disabled={triageLoading}
                sx={{ textTransform: 'none' }}
              >
                Auto-Classify Level
              </Button>
              {(triageLoading || triageSaving) && <CircularProgress size={16} />}
              <Tooltip
                arrow
                placement="top-start"
                title={
                  <Box sx={{ maxWidth: 460, p: 0.5 }}>
                    {displayTriageLevels.map((l) => (
                      <Box key={l.level} sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          {l.label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Clinical: {l.clinical_presentation}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          ED: {l.ed_disposition}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', color: '#012676' }}>
                  <InfoOutlinedIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Level definitions
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
            {triageAssessment?.trigger_flags_json?.length ? (
              <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {triageAssessment.trigger_flags_json.slice(0, 6).map((f) => (
                  <Chip key={f} size="small" label={f} variant="outlined" color="warning" />
                ))}
              </Box>
            ) : null}
          </Grid>

          {modifierOptions.length > 0 && (
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Diagnosis Specifier"
                value={selectedPmoId === '' ? '' : String(selectedPmoId)}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = value ? Number(value) : '';
                  setSelectedPmoId(parsed);
                  updateField(TAB_NAME, 'primary_diagnosis_modifier', value);
                  if (selectedDisorder) {
                    void resolveMapping(selectedDisorder.disorder_id, parsed === '' ? null : parsed);
                  }
                }}
                helperText={selectedModifierLabel || undefined}
                size="small"
                fullWidth
                sx={formFieldSx}
              >
                <MenuItem value="">None</MenuItem>
                {modifierOptions.map((m) => (
                  <MenuItem key={m.pmo_id} value={String(m.pmo_id)}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {resolution?.primary_code ? (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                ICD-11 mapping source: {resolution.primary_source}
              </Typography>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="text"
                startIcon={<ValidateIcon color="success" />}
                onClick={() => void saveDiagnosisSelection(true)}
                disabled={!selectedDisorder || resolveLoading}
                sx={{ textTransform: 'none', color: '#2e7d32', fontWeight: 600 }}
              >
                Validate
              </Button>
              <Button
                variant="text"
                startIcon={<RejectIcon color="error" />}
                onClick={() => void saveDiagnosisSelection(false)}
                disabled={!selectedDisorder || resolveLoading}
                sx={{ textTransform: 'none', color: '#c62828', fontWeight: 600 }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={addSecondaryDiagnosis}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#1976d2',
                  color: '#ffffff',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#115293',
                    boxShadow: 'none',
                  },
                  '&:active': {
                    bgcolor: '#0d47a1',
                  },
                }}
              >
                Add Secondary Diagnosis
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void triggerDiagnosisRun()}
                disabled={dxLoading}
                sx={{ textTransform: 'none' }}
              >
                Recompute AI Diagnosis
              </Button>
              {resolveLoading && <CircularProgress size={16} />}
              {dxLoading && <CircularProgress size={16} />}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                Clinician Finalized Diagnoses
              </Typography>
              {savedSelections.length === 0 && dxFinalSelections.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No finalized selections yet. Validate or reject the DSM primary above, or use Primary / Secondary on an AI candidate.
                </Typography>
              ) : (
                <TableContainer sx={{ border: `1px solid ${SECTION_BORDER}`, borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f9ff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Slot</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Diagnosis</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Decision</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {savedSelections
                        .slice()
                        .sort((a, b) => a.slot.localeCompare(b.slot))
                        .map((s) => {
                          const isRejected = s.ai_suggestion_accepted === false;
                          return (
                            <TableRow key={`dsm-${s.id}`} hover>
                              <TableCell>DSM</TableCell>
                              <TableCell>{s.slot}</TableCell>
                              <TableCell>{s.dsmv_disorder_id ? `DSM disorder #${s.dsmv_disorder_id}` : '-'}</TableCell>
                              <TableCell>{s.diagnosis_status || 'n/a'}</TableCell>
                              <TableCell>{s.icd11_code || s.icd10_code || 'n/a'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={isRejected ? 'Rejected' : 'Validated'}
                                  color={isRejected ? 'error' : 'success'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Button size="small" color="success" onClick={() => void redoSelectionDecision(s, true)}>
                                    Validate
                                  </Button>
                                  <Button size="small" color="error" onClick={() => void redoSelectionDecision(s, false)}>
                                    Reject
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {dxFinalSelections
                        .slice()
                        .sort((a, b) => a.slot.localeCompare(b.slot))
                        .map((f) => {
                          const st = (f.status || '').toLowerCase();
                          const aiRejected = f.accepted_ai === false || st === 'rejected';
                          const aiConfirmed = st === 'confirmed' || st === 'finalized';
                          const aiProvisional = st === 'provisional';
                          const label =
                            f.selected_condition_title ||
                            f.selected_icd11_title ||
                            (f.selected_condition_id != null ? `Condition #${f.selected_condition_id}` : '—');
                          const code = f.selected_icd11_code || f.selected_condition_code || 'n/a';
                          const statusLabel =
                            st === 'rejected'
                              ? 'Rejected'
                              : st === 'confirmed' || st === 'finalized'
                                ? 'Confirmed'
                                : st === 'provisional'
                                  ? 'Provisional'
                                  : f.status || 'n/a';
                          let decisionLabel = '—';
                          let decisionColor: 'error' | 'success' | 'warning' | 'default' = 'default';
                          if (aiRejected) {
                            decisionLabel = 'Rejected';
                            decisionColor = 'error';
                          } else if (aiConfirmed) {
                            decisionLabel = 'Confirmed';
                            decisionColor = 'success';
                          } else if (aiProvisional) {
                            decisionLabel = 'Provisional';
                            decisionColor = 'warning';
                          }
                          return (
                            <TableRow key={`dx-${f.id}`} hover>
                              <TableCell>AI</TableCell>
                              <TableCell>{f.slot}</TableCell>
                              <TableCell>{label}</TableCell>
                              <TableCell>{statusLabel}</TableCell>
                              <TableCell>{code}</TableCell>
                              <TableCell>
                                <Chip size="small" label={decisionLabel} color={decisionColor} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    color="success"
                                    disabled={aiConfirmed}
                                    onClick={() => void redoDxFinalSelection(f, true)}
                                  >
                                    Validate
                                  </Button>
                                  <Button size="small" color="error" onClick={() => void redoDxFinalSelection(f, false)}>
                                    Reject
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                AI Candidate Evidence
              </Typography>
              {dxRun ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Latest run: {dxRun.status} ({new Date(dxRun.started_at).toLocaleString()})
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  No AI run yet for this session.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Use Primary or Secondary to record an AI condition in the list below. DSM primary fields are not auto-filled from AI.
              </Typography>

              {dxCandidates.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No candidates available.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dxCandidates.slice(0, 5).map((c) => (
                    <Paper key={c.candidate_id} variant="outlined" sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.condition_title} ({c.condition_code}) - score {Number(c.score || 0).toFixed(2)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button size="small" color="success" onClick={() => void applyCandidateAsFinal(c, 'primary')}>
                            Primary
                          </Button>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={() => void applyCandidateAsFinal(c, 'secondary')}
                          >
                            Secondary
                          </Button>
                          <Button size="small" color="error" onClick={() => void applyCandidateAsFinal(c, 'reject')}>
                            Reject
                          </Button>
                        </Box>
                      </Box>
                      {c.maps?.length ? (
                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {c.maps.slice(0, 3).map((m) => (
                            <Chip
                              key={`${c.candidate_id}-${m.mapped_condition_id}`}
                              size="small"
                              label={`${m.mapped_code} - ${m.mapped_title}`}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : null}
                    </Paper>
                  ))}
                </Box>
              )}

            </Paper>
          </Grid>

          {secondaryDiagnoses.map((entry) => (
            <Grid item xs={12} key={entry.slot}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={dsmOptions}
                      loading={dsmLoading}
                      value={entry.selectedDisorder}
                      isOptionEqualToValue={(a, b) => a.disorder_id === b.disorder_id}
                      getOptionLabel={(o) =>
                        o.disorder_name
                          ? `${o.disorder_name} (${o.icd_11 || o.icd_10 || 'code n/a'})`
                          : `#${o.disorder_id}`
                      }
                      onChange={async (_, value) => {
                        updateSecondary(entry.slot, {
                          selectedDisorder: value,
                          dsmInput: value?.disorder_name || '',
                          selectedPmoId: '',
                          resolution: null,
                          message: null,
                        });
                        if (!value) return;
                        await resolveSecondary(entry.slot, value.disorder_id, null);
                      }}
                      inputValue={entry.dsmInput}
                      onInputChange={(_, value) => {
                        updateSecondary(entry.slot, { dsmInput: value });
                        if ((value || '').trim().length < 2) return;
                        setSecondarySearchLoadingSlot(entry.slot);
                        void fetchDsmOptions(value).finally(() => {
                          setSecondarySearchLoadingSlot((prev) => (prev === entry.slot ? null : prev));
                        });
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Secondary diagnosis"
                          size="small"
                          placeholder="Search DSM diagnosis"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {secondarySearchLoadingSlot === entry.slot ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={formFieldSx}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Status"
                      value={entry.status}
                      onChange={(e) => updateSecondary(entry.slot, { status: e.target.value })}
                      sx={formFieldSx}
                    >
                      <MenuItem value="Provisional">Provisional</MenuItem>
                      <MenuItem value="Confirmed">Confirmed</MenuItem>
                      <MenuItem value="Differential">Differential</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="ICD-11"
                      value={entry.resolution?.primary_code || ''}
                      InputProps={{ readOnly: true }}
                      sx={formFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', height: '100%' }}>
                      <Button size="small" variant="outlined" onClick={() => void saveSecondary(entry)}>
                        Save
                      </Button>
                      <Button size="small" color="error" onClick={() => void removeSecondary(entry.slot)}>
                        Remove
                      </Button>
                      {entry.loading && <CircularProgress size={14} />}
                    </Box>
                  </Grid>
                  {entry.message ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {entry.message}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>
            </Grid>
          ))}

          {resolution?.alternates?.length ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                ICD-11 alternates:
              </Typography>
              <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
                {resolution.alternates.map((item) => (
                  <Chip
                    key={`${item.icd11_code}-${item.map_priority}`}
                    size="small"
                    label={`${item.icd11_code}${item.icd11_title ? ` - ${item.icd11_title}` : ''}`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </Paper>
      )}

      {/* Lab Report Upload Dialog - Lab Report Upload Dialog */}
      <Dialog
        open={labUploadOpen}
        onClose={() => setLabUploadOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabIcon sx={{ color: 'secondary.main' }} />
            Upload Lab Report
          </Box>
          <IconButton onClick={() => setLabUploadOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <LabReportUpload
            sessionId={sessionId}
            onUploadComplete={() => {
              // Keep dialog open so user can see results;
              // form fields update via WebSocket automatically
            }}
          />
        </DialogContent>
      </Dialog>   
    </Box>
  );
}
